import { Application } from "pixi.js";
import { analyzeAudio } from "./analysis/analyze";
import { Game } from "./game/Game";
import { showLoading, showReady, showResults, showRotateGate, showTitle } from "./ui/screens";

/** Best-effort fullscreen + landscape lock (Android). iOS falls back to the rotate gate. */
async function tryLandscape(): Promise<void> {
  try {
    await document.documentElement.requestFullscreen({ navigationUI: "hide" });
  } catch {
    /* iOS Safari: no fullscreen API for pages */
  }
  try {
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (o: string) => Promise<void>;
    };
    await orientation.lock?.("landscape");
  } catch {
    /* lock unsupported without fullscreen or on iOS */
  }
}

let app: Application | null = null;
let audioCtx: AudioContext | null = null;

async function getApp(): Promise<Application> {
  if (app) return app;
  app = new Application();
  await app.init({
    resizeTo: window,
    background: 0x0a0a1a,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  document.getElementById("app")!.appendChild(app.canvas);
  return app;
}

function songNameOf(file: File): string {
  return file.name.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ");
}

function flowTitle(): void {
  const close = showTitle(async (file) => {
    close();
    // Go fullscreen as early as possible (needs a user gesture).
    void tryLandscape();
    // Create/resume the AudioContext inside the user gesture (iOS requirement).
    audioCtx ??= new AudioContext();
    if (audioCtx.state === "suspended") await audioCtx.resume();
    await flowAnalyze(file);
  });
}

/** Dev/demo shortcut: ?song=demo loads the bundled synthetic track. */
function maybeDemoSong(): boolean {
  const song = new URLSearchParams(location.search).get("song");
  if (!song || !/^[\w-]+$/.test(song)) return false;
  void (async () => {
    const res = await fetch(`/${song}.mp3`);
    if (!res.ok) {
      flowTitle();
      return;
    }
    const blob = await res.blob();
    audioCtx ??= new AudioContext();
    await flowAnalyze(new File([blob], `${song}.mp3`, { type: "audio/mpeg" }));
  })();
  return true;
}

async function flowAnalyze(file: File): Promise<void> {
  const songName = songNameOf(file);
  const loading = showLoading(songName);
  try {
    const bytes = await file.arrayBuffer();
    loading.setProgress("Decoding audio", 0.02);
    const buffer = await audioCtx!.decodeAudioData(bytes);
    const track = await analyzeAudio(buffer, (stage, value) => loading.setProgress(stage, 0.05 + value * 0.95));
    loading.close();

    const closeReady = showReady(songName, track.bpmEstimate, track.notes.length, track.seed, async () => {
      closeReady();
      await tryLandscape();
      const closeGate = showRotateGate();
      const pixi = await getApp();
      if (audioCtx!.state === "suspended") await audioCtx!.resume();
      const game = new Game(pixi, track, audioCtx!, buffer, `${songName}`, (stats) => {
        game.destroy();
        closeGate();
        showResults(songName, stats, () => {
          location.reload();
        });
      });
      game.start();
    });
  } catch (err) {
    loading.close();
    alert(`Could not analyze that file: ${err instanceof Error ? err.message : err}`);
    flowTitle();
  }
}

if (!maybeDemoSong()) flowTitle();
